import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {
  BookOpenIcon,
  SparklesIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  HomeIcon,
  AcademicCapIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import HeaderNav from '../HeaderNav';
import './Learning.css';

const PAGE_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const LearningAndGamesHub = ({ userId, user, onLogout }) => {
  const [gameUnlocks, setGameUnlocks] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [unlocksRes, statsRes] = await Promise.all([
          axios.get('/api/game/city-of-signals/unlocks').catch(() => ({ data: null })),
          userId
            ? axios.get(`/api/learning/stats/${userId}`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        setGameUnlocks(unlocksRes.data);
        setUserStats(statsRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  return (
    <div className="min-h-screen bg-bone">
      {user && (
        <HeaderNav
          activeSection="learn-and-play"
          onSectionChange={() => {}}
          user={user}
          onLogout={onLogout}
          visionCount={0}
        />
      )}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-secondary hover:text-primary mb-8 transition-colors duration-300"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <HomeIcon className="w-5 h-5" />
          <span className="text-body">Back to dashboard</span>
        </button>

        <motion.div {...PAGE_MOTION} className="mb-10">
          <h1 className="font-display text-h1 text-primary mb-2">Learn & play</h1>
          <p className="text-body text-secondary">
            Build your financial knowledge and put it into practice with Aura.
          </p>
        </motion.div>

        <motion.section
          {...PAGE_MOTION}
          transition={{ ...PAGE_MOTION.transition, delay: 0.05 }}
          className="surface-panel p-6 md:p-8 mb-10"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg border border-hairline bg-pine-tint flex items-center justify-center">
              <LightBulbIcon className="w-5 h-5 text-pine" />
            </div>
            <h2 className="font-display text-h2 text-primary">How learning & games work together</h2>
          </div>
          <p className="text-body text-secondary mb-5 leading-relaxed">
            On Aura, learning and games support each other. You learn concepts in structured paths,
            then apply them in realistic scenarios in Aura City of Lights.
          </p>
          <ol className="space-y-4 mb-5">
            {[
              {
                title: 'Learning unlocks game levels',
                body: 'Complete the Basic learning path to get the most out of Level 1. Finish Intermediate to unlock Level 2, and Advanced to unlock Level 3.',
              },
              {
                title: 'Games reinforce what you learn',
                body: 'In City of Lights you make choices about spending, saving, and risk — practicing trade-offs in a safe, structured way.',
              },
              {
                title: 'Everything ties to your goals',
                body: 'Learning topics and game scenarios align with real-world skills: budgeting, investing basics, risk, and planning.',
              },
            ].map((item, i) => (
              <li key={i} className="flex gap-3">
                <span className="figure font-display text-pine tabular-nums shrink-0">{i + 1}.</span>
                <div>
                  <p className="text-body font-medium text-primary">{item.title}</p>
                  <p className="text-label text-secondary mt-0.5">{item.body}</p>
                </div>
              </li>
            ))}
          </ol>
          {!loading && gameUnlocks?.learning && (
            <p className="text-label text-secondary pt-4 border-t border-hairline">
              Your progress:{' '}
              <span className="figure text-primary tabular-nums">
                {gameUnlocks.learning.completed_count} of {gameUnlocks.learning.total_paths}
              </span>{' '}
              learning paths complete.
            </p>
          )}
        </motion.section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <motion.div {...PAGE_MOTION} transition={{ ...PAGE_MOTION.transition, delay: 0.1 }}>
            <Link to="/learning" className="block h-full surface-panel p-6 hover:bg-pine-tint/30 transition-colors duration-300 group">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg border border-hairline bg-pine-tint flex items-center justify-center">
                  <AcademicCapIcon className="w-6 h-6 text-pine" />
                </div>
                <div>
                  <h3 className="font-display text-h2 text-primary group-hover:text-pine transition-colors duration-300">
                    Learning hub
                  </h3>
                  <p className="text-label text-secondary">Structured paths & lessons</p>
                </div>
              </div>
              <p className="text-label text-secondary mb-4">
                Work through Basic, Intermediate, and Advanced paths with lessons and quizzes.
              </p>
              <div className="flex items-center justify-between">
                {userStats && (
                  <span className="text-label text-faint">
                    <span className="figure tabular-nums">{userStats.total_completed}</span> completed ·{' '}
                    <span className="figure tabular-nums">{userStats.badges?.length || 0}</span> badges
                  </span>
                )}
                <span className="text-label text-pine font-medium flex items-center gap-1">
                  Open learning <ArrowRightIcon className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </motion.div>

          <motion.div {...PAGE_MOTION} transition={{ ...PAGE_MOTION.transition, delay: 0.15 }}>
            <Link
              to="/game/city-of-signals"
              className="block h-full surface-panel p-6 hover:bg-pine-tint/30 transition-colors duration-300 group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg border border-hairline bg-pine-tint flex items-center justify-center">
                  <SparklesIcon className="w-6 h-6 text-pine" />
                </div>
                <div>
                  <h3 className="font-display text-h2 text-primary">Aura City of Lights</h3>
                  <p className="text-label text-secondary">Apply your knowledge in play</p>
                </div>
              </div>
              <p className="text-label text-secondary mb-4">
                Make financial choices in a city full of signals. Level 1 is always available; complete
                learning paths to unlock more levels.
              </p>
              <div className="flex items-center justify-between">
                {gameUnlocks?.learning && (
                  <span className="text-label text-faint">
                    <span className="figure tabular-nums">
                      {gameUnlocks.learning.completed_count}/{gameUnlocks.learning.total_paths}
                    </span>{' '}
                    paths → more levels
                  </span>
                )}
                <span className="text-label text-pine font-medium flex items-center gap-1">
                  Play game <ArrowRightIcon className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>

        <motion.div {...PAGE_MOTION} transition={{ ...PAGE_MOTION.transition, delay: 0.2 }} className="text-center">
          <Link
            to="/learning/paths"
            className="inline-flex items-center gap-2 text-label text-secondary hover:text-primary transition-colors duration-300"
          >
            <BookOpenIcon className="w-4 h-4" />
            Browse all learning paths
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default LearningAndGamesHub;
